import {Link, useLocation, useNavigate} from "react-router-dom";
import {useCallback, useEffect, useRef, useState} from "react";
import {BsSearch} from "react-icons/bs";
import axios from "axios";
import {useQuery} from "react-query";
import {EmptyState} from "./EmptyState";
import {StatusCard} from "./StatusCard";
import {TableData} from "./TableData";
import {getStatusType} from "../utils/status";
import moment from "moment";

const TABLE_HEAD = [
  "Date",
  "Created at",
  "Order ID",
  "Account Name",
  "Pickup Address",
  "Phone number",
  "Delivery Category",
  "Price",
  "Status",
  "Action",
];

export const HubOrders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const [limit, setLimit] = useState(10);

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  const getOrders = useCallback(async () => {
    let url = `${Baseurl}/api/admin/orders?limit=${limit}&page=${currentPage}&filter=orderStatus:IN_HUB&sort=paidAt:DESC`;

    if (searchQuery) {
      url += `&query=${encodeURIComponent(searchQuery)}`;
    }

    const response = await axios.get(url, {...config});
    setTotalPages(Math.ceil(response?.data?.data?.meta?.total / limit));
    return response?.data?.data?.items;
  }, [searchQuery, currentPage, limit, Baseurl, config]);

  const {
    data: orderData,
    isLoading: isLoadingOrderData,
    refetch,
  } = useQuery(["HUB_ORDERS", currentPage, searchQuery, limit], getOrders);

  // Custom debounce function for search
  const debounceSearch = useCallback((value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      // Reset to first page when search changes
      handlePageChange(1);
    }, 300);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debounceSearch(value);
  };

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full !rounded-lg !border !border-gray-200">
      <div className="flex !items-center !justify-between !px-6 !py-4">
        <h3 className="!text-xl !font-bold !text-gray-700">Hub Orders</h3>

        <div className="flex !gap-3">
          <div className="relative !h-12 !bg-gray-50 !border !rounded-lg !w-96 !border-gray-300">
            <input
              type="text"
              placeholder="Search orders"
              className="w-full h-full !pl-12 !text-sm !bg-transparent !outline-none !placeholder:text-gray-400"
              defaultValue={searchQuery}
              onChange={handleSearchChange}
            />
            <BsSearch className="absolute left-4 top-3.5 text-gray-400" />
          </div>
        </div>
      </div>
      {isLoadingOrderData ? (
        <div className="!py-14">
          <EmptyState type="loading" text="Loading orders..." />
        </div>
      ) : orderData?.length > 0 ? (
        <>
          <div className="!overflow-auto">
            <table className="!w-full">
              <thead className="!bg-gray-50 !border-b !border-gray-200">
                <tr>
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="!p-3 !text-xs !font-medium !text-gray-500"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderData.map((order: any) => {
                  const status = getStatusType(order.orderStatus);
                  return (
                    <tr key={order.id} className="!border-b !border-gray-100">
                      <TableData
                        content={
                          order.paidAt
                            ? new Date(order.paidAt).toLocaleDateString()
                            : "N/A"
                        }
                      />
                      <TableData
                        content={
                          order.createdAt ? (
                            <>
                              {moment(order.createdAt).format("DD MMM, YYYY")}
                              <br />
                              {moment(order.createdAt).format("hh:mmA")}
                            </>
                          ) : (
                            "N/A"
                          )
                        }
                      />

                      <TableData content={order.orderId} />
                      <TableData
                        content={
                          order.user && order?.user?.businessName
                            ? order?.user?.businessName
                            : "N/A"
                        }
                      />

                      <TableData
                        content={`${order.pickUpDetail.fullAddress.substring(
                          0,
                          30
                        )}...`}
                      />
                      <TableData content={order.pickUpDetail.phoneNumber} />
                      <TableData content={order.deliveryType} />
                      <TableData content={`₦${order.estimatedCost}`} />
                      <td className="!p-5">
                        <StatusCard title={status.title} type={status.type} />
                      </td>
                      <td className="!p-5">
                        <Link
                          to={`/orders/${order.id}`}
                          className="!text-sm !font-semibold !text-orange-500 hover:!text-orange-600"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                {/* {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === page
                      ? "bg-orange-500 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))} */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>

              <select
                name=""
                id=""
                onChange={(
                  e: React.ChangeEvent<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                  >
                ) => {
                  setLimit(Number(e.target.value));
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div className="!py-14">
          <EmptyState
            type="order"
            text={searchQuery ? "No orders match your search" : "No Orders Yet"}
          />
        </div>
      )}
    </div>
  );
};
